import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { actionUserLogout } from '@shared-core/features/auth/actions/global'
import { handleErrors } from '@shared-core/features/notifications/actions'
import { TIMEOUT } from '@shared-core/features/notifications/configs/notifications'
import type {
  AntifraudPrepareError,
  ParsedError,
  PayError,
  Service,
  Toast,
} from '@shared-core/features/notifications/models'
import { hasSpecificServiceUnavailable } from '@shared-core/features/notifications/utils/service-unavailable'
import { API_KNOWN_ERRORS } from '@shared-core/services/api'

// Файл агрегирует логику toast-очереди и системных флагов ошибок для UI.
export const storeKey = '@redux/notifications'

// Сравнивает текст двух тостов. Текст бывает строкой или объектом ошибки { error, value }.
function isSameToastText(a: Toast['text'], b: Toast['text']): boolean {
  if (typeof a === 'string' || typeof b === 'string') {
    return a === b
  }

  return a.error === b.error && a.value === b.value
}

// Тост уже виден, если есть незакрытый тост того же типа с тем же текстом.
function isDuplicateVisibleToast(toasts: Toast[], toast: Toast): boolean {
  return toasts.some((item) => !item.isHidden && item.type === toast.type && isSameToastText(item.text, toast.text))
}

export type NotificationsState = {
  toasts: Toast[]

  sbpPrepareError: PayError | boolean
  // Gate на prepare (СБП/вывод/своп/…) — штора вместо toast.
  antifraudPrepareError: AntifraudPrepareError | null
  invalidToken: boolean
  // Бэк сам отозвал сессию (подтверждено удаление аккаунта): 401 от параллельных запросов не должны
  // разлогинивать раньше, чем пользователь увидит финальный экран. Сбрасывается на logout
  isSessionRevoked: boolean
  KYC2IsRequired: boolean
  serviceIsUnavailable: Service | boolean
  isServiceSheetOpen: boolean
  telegramAlreadyLinkedToAnotherAccount: boolean
}

type NotificationBooleanSheet = {
  [K in keyof NotificationsState]: NotificationsState[K] extends boolean ? K : never
}[keyof NotificationsState]

const initialState: NotificationsState = {
  toasts: [],
  sbpPrepareError: false,
  antifraudPrepareError: null,
  invalidToken: false,
  isSessionRevoked: false,
  KYC2IsRequired: false,
  serviceIsUnavailable: false,
  isServiceSheetOpen: false,
  telegramAlreadyLinkedToAnotherAccount: false,
}

export const whitelist: string[] = []

export const notificationsSlice = createSlice({
  name: storeKey,
  initialState,
  reducers: {
    addItemToast: (state: NotificationsState, action: PayloadAction<Toast>) => {
      const incoming = action.payload

      // Спам-защита: одинаковый тост при частом нажатии не дублируем, чтобы они не мельтешили.
      if (isDuplicateVisibleToast(state.toasts, incoming)) {
        return
      }

      if (incoming.override) {
        ;(state.toasts || []).forEach((item) => {
          item.isHidden = true
        })
      }

      state.toasts.push(incoming)
    },
    hideItemToast: (state: NotificationsState, action: PayloadAction<string>) => {
      const findItem = state.toasts.find((item) => item.uuid === action.payload)

      if (findItem) {
        findItem.isHidden = true
      }
    },
    removeItemToast: (state: NotificationsState, action: PayloadAction<string>) => {
      const index = state.toasts.findIndex((item) => item.uuid === action.payload)

      if (index > -1) {
        state.toasts.splice(index, 1)
      }
    },
    removeAllToasts: (state: NotificationsState) => {
      ;(state.toasts || []).forEach((item) => {
        item.isHidden = true
      })
    },
    openBottomSheet: (
      state: NotificationsState,
      action: PayloadAction<{
        sheet: NotificationBooleanSheet
      }>,
    ) => {
      state[action.payload.sheet] = true
    },
    closeBottomSheet: (
      state: NotificationsState,
      action: PayloadAction<{
        sheet: NotificationBooleanSheet
      }>,
    ) => {
      state[action.payload.sheet] = false
    },
    setServiceIsUnavailable: (state: NotificationsState, action: PayloadAction<Service | boolean>) => {
      const payload = action.payload

      // Global degradation не перебивает конкретный сервис и не открывается поверх уже показанного sheet.
      if (
        payload === 'global' &&
        (hasSpecificServiceUnavailable(state.serviceIsUnavailable) || state.isServiceSheetOpen)
      ) {
        return
      }

      // Sheet уже открыт — обновляем только контент (steam вместо global), без второго navigate.
      if (state.isServiceSheetOpen && typeof payload === 'string') {
        state.serviceIsUnavailable = payload
        return
      }

      if (state.isServiceSheetOpen) {
        return
      }

      if (payload === 'global' && hasSpecificServiceUnavailable(state.serviceIsUnavailable)) {
        return
      }

      state.serviceIsUnavailable = payload
    },
    setServiceSheetOpen: (state: NotificationsState, action: PayloadAction<boolean>) => {
      state.isServiceSheetOpen = action.payload
    },
    setTelegramAlreadyLinkedToAnotherAccount: (state: NotificationsState, action: PayloadAction<boolean>) => {
      state.telegramAlreadyLinkedToAnotherAccount = action.payload
    },
    setSbpPrepareError: (state: NotificationsState, action: PayloadAction<PayError | boolean>) => {
      state.sbpPrepareError = action.payload
    },
    clearAntifraudPrepareError: (state: NotificationsState) => {
      state.antifraudPrepareError = null
    },
    setSessionRevoked: (state: NotificationsState, action: PayloadAction<boolean>) => {
      state.isSessionRevoked = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      handleErrors,
      (
        state: NotificationsState,
        action: PayloadAction<{ errors: ParsedError[]; service?: Service; antifraudCaseId?: string | null }>,
      ) => {
        // Нормализуем список ошибок в состояние уведомлений, избегая дубликатов toast.
        const { errors, service, antifraudCaseId } = action.payload

        // Учитываем и гаснущие (isHidden) тосты: фоновые 429 в окне hide→remove иначе
        // плодят второй тост с тем же uuid (= error), что даёт коллизию React-ключей и тост,
        // у которого не взводится таймер авто-закрытия — «не всегда закрывается».
        const flattedErrors = state.toasts.reduce<API_KNOWN_ERRORS[]>((acc, item) => {
          if (typeof item.text !== 'string') {
            acc.push(item.text.error)
          }
          return acc
        }, [])

        const antifraudErrors = errors.map((item) => item.error).filter((error): error is API_KNOWN_ERRORS => false)

        if (antifraudErrors.length > 0) {
          state.antifraudPrepareError = {
            errors: antifraudErrors,
            antifraudCaseId: antifraudCaseId ?? null,
          }
        }

        // Проходим по API-ошибкам и раскладываем их по state-полям/тостам без дублирования.
        const result = errors.reduce<NotificationsState>(
          (acc, item) => {
            if (item.error === API_KNOWN_ERRORS.INVALID_TOKEN) {
              acc.invalidToken = !state.isSessionRevoked
              return acc
            }

            if (
              item.error === API_KNOWN_ERRORS.KYC_2_LEVEL_REQUIRED ||
              item.error === API_KNOWN_ERRORS.KYC_LEVEL_REQUIRED
            ) {
              acc.KYC2IsRequired = true
              return acc
            }

            // Штора верификации на экране, без toast.
            if (
              item.error === API_KNOWN_ERRORS.ONLY_RUSSIAN_PASSPORT_ALLOWED ||
              item.error === API_KNOWN_ERRORS.PASSPORT_DOCUMENT_REQUIRED
            ) {
              return acc
            }

            if (item.error === API_KNOWN_ERRORS.TG_ALREADY_LINKED_TO_ANOTHER_ACCOUNT) {
              acc.telegramAlreadyLinkedToAnotherAccount = true
              return acc
            }

            if (service && item.error === API_KNOWN_ERRORS.SERVICE_TEMPORARILY_UNAVAILABLE) {
              acc.serviceIsUnavailable = service
              return acc
            }

            if (
              service === 'sbp' &&
              [
                API_KNOWN_ERRORS.PAYMENT_AMOUNT_EXCEEDS_LIMIT_ERROR,
                API_KNOWN_ERRORS.NO_AGENT_AVAILABLE,
                API_KNOWN_ERRORS.REQUEST_ERROR,
                API_KNOWN_ERRORS.UNPROCESSABLE_ENTITY,
                API_KNOWN_ERRORS.INTERNAL_FAILED_TO_PREPARE_PAYMENT,
              ].includes(item.error)
            ) {
              acc.sbpPrepareError = {
                reason: item.error,
                maxAmountAllowed: item.value,
              }
              return acc
            }

            if (!flattedErrors.includes(item.error)) {
              acc.toasts.push({
                text: item,
                type: 'error',
                uuid: item.error,
                timeout: TIMEOUT.DEFAULT,
                isClosable: true,
              })
            }

            return acc
          },
          {
            toasts: [],
            sbpPrepareError: false,
            antifraudPrepareError: null,
            KYC2IsRequired: false,
            invalidToken: false,
            isSessionRevoked: state.isSessionRevoked,
            serviceIsUnavailable: false,
            isServiceSheetOpen: false,
            telegramAlreadyLinkedToAnotherAccount: false,
          },
        )

        state.toasts.push(...result.toasts)

        // KYC-гейт: только взводим, сброс — после открытия шторки (closeBottomSheet).
        if (result.KYC2IsRequired) {
          state.KYC2IsRequired = true
        }

        state.invalidToken = result.invalidToken
        state.sbpPrepareError = result.sbpPrepareError
        state.telegramAlreadyLinkedToAnotherAccount = result.telegramAlreadyLinkedToAnotherAccount

        if (!state.isServiceSheetOpen && result.serviceIsUnavailable) {
          state.serviceIsUnavailable = result.serviceIsUnavailable
        }
      },
    )

    builder.addCase(actionUserLogout, (state: NotificationsState) => {
      Object.assign(state, initialState)
    })
  },
})

export const {
  addItemToast,
  removeItemToast,
  removeAllToasts,
  hideItemToast,
  openBottomSheet,
  closeBottomSheet,
  setServiceIsUnavailable,
  setServiceSheetOpen,
  setTelegramAlreadyLinkedToAnotherAccount,
  setSbpPrepareError,
  clearAntifraudPrepareError,
  setSessionRevoked,
} = notificationsSlice.actions
